import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { io } from "socket.io-client";
import Navbar from "./Components/Navbar/Navbar";

// PUBLIC COMPONENTS
import Hero from "./Components/Hero/Hero";
import Login from "./Components/Login/Login";
import PrivacyPolicy from "./Components/Privacypolicy/Privacypolicy";
import Terms from "./Components/Terms/Terms";
import ResetPassword from "./Components/ResetPassword/ResetPassword";

// PROTECTED COMPONENTS
import Circular from "./Components/Circular/Circular";
import CircularPage from "./Components/Circularpage/Circularpage";
import Announcement from "./Components/Announcement/Announcement";
import AnnouncementPage from "./Components/AnnouncementPage/AnnouncementPage";
import AttendanceReport from "./Components/AttendanceReport/AttendanceReport";
import PerformanceReport from "./Components/PerformanceReport/PerformanceReport";
import ClassSectionDetails from "./Components/PerformanceReport/ClassSectionDetails";
import Enquiries from "./Components/Enquiries/Enquiries";
import AddEnquiry from "./Components/AddEnquiry/AddEnquiry";
import TeacherDashboard from "./Components/TeacherApp/TeacherDashboard/TeacherDashboard";
import ChairmanHero from "./Components/chairman/Hero/Hero";

import "./App.css";

// -------------------- SOCKET.IO SETUP --------------------
const socket = io("http://localhost:3001"); // Replace with your server URL

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState("");

  // -------------------- INITIAL SYNC --------------------
  useEffect(() => {
    syncAuth();
  }, []);

  // -------------------- LISTEN FOR LOCALSTORAGE CHANGES --------------------
  useEffect(() => {
    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, []);

  // -------------------- LISTEN FOR SOCKET UPDATES --------------------
  useEffect(() => {
    // When any device emits an update, sync auth
    socket.on("authUpdated", () => {
      syncAuth();
    });

    return () => {
      socket.off("authUpdated");
    };
  }, []);

  const syncAuth = () => {
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    const role = localStorage.getItem("userRole") || "";
    setIsLoggedIn(loggedIn);
    setUserRole(role);
  };

  // -------------------- LOGIN / LOGOUT --------------------
  const handleLogin = (role) => {
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("userRole", role);
    setIsLoggedIn(true);
    setUserRole(role);

    // Emit socket event so all other devices update immediately
    socket.emit("authUpdated");
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setUserRole("");

    // Emit socket event so all other devices update immediately
    socket.emit("authUpdated");
  };

  // -------------------- PROTECTED ROUTE WRAPPER --------------------
  const ProtectedRoute = ({ children }) => {
    const currentlyLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    return currentlyLoggedIn ? children : <Navigate to="/login" replace />;
  };

  const AuthLayout = () => (
    <>
      <Navbar isLoggedIn={isLoggedIn} onLogout={handleLogout} />
      <div className="main-content">
        <Outlet />
      </div>
    </>
  );

  return (
    <Routes>
      {/* ───────── PUBLIC ROUTES ───────── */}
      <Route path="/login" element={<Login onLogin={handleLogin} />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<Terms />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />

      {/* QR Code / Public Enquiry */}
      <Route path="/add-enquiry" element={<AddEnquiry isPublic={true} />} />

      {/* ───────── PROTECTED ROUTES ───────── */}
      <Route element={<ProtectedRoute><AuthLayout /></ProtectedRoute>}>
        {/* ADMIN & CHAIRMAN */}
        {(userRole === "admin" || userRole === "chairman") && (
          <>
            <Route
              path="/"
              element={
                userRole === "admin" ? (
                  <Hero activePage="Dashboard" />
                ) : (
                  <ChairmanHero activePage="Dashboard" />
                )
              }
            />
            <Route path="/circulars" element={<Circular />} />
            <Route path="/circularpage" element={<CircularPage />} />
            <Route path="/announcements" element={<Announcement />} />
            <Route path="/announcementpage" element={<AnnouncementPage />} />
            <Route path="/attendancereport" element={<AttendanceReport />} />
            <Route path="/performancereport" element={<PerformanceReport />} />
            <Route
              path="/class-section/:classValue/:sectionValue"
              element={<ClassSectionDetails />}
            />
            <Route path="/enquiries" element={<Enquiries />} />
            <Route path="/enquiry/add" element={<AddEnquiry isPublic={false} />} />
          </>
        )}

        {/* TEACHER */}
        {userRole === "teacher" && (
          <>
            <Route path="/" element={<TeacherDashboard />} />
            <Route path="/performancereport" element={<PerformanceReport />} />
            <Route
              path="/class-section/:classValue/:sectionValue"
              element={<ClassSectionDetails />}
            />
          </>
        )}
      </Route>

      {/* ───────── FALLBACK ROUTE ───────── */}
      <Route
        path="*"
        element={
          <Navigate
            to={localStorage.getItem("isLoggedIn") === "true" ? "/" : "/login"}
            replace
          />
        }
      />
    </Routes>
  );
}

export default App;
